import React, { useEffect, useState } from 'react'

export default function GallerySection({ gallerySlug = 'rekruttering-og-kontor' }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(null)

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
        if (res.ok) {
          const data = await res.json()
          const items = data.items || []
          const found = items.find(
            (g) => g.galleri_slug === gallerySlug || g.slug === gallerySlug
          )
          setGallery(found || items[0] || null)
        }
      } catch (err) {
        console.error('Failed to load gallery', err)
      } finally {
        setLoading(false)
      }
    }
    fetchGallery()
  }, [gallerySlug])

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-4 border-sky-600 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (!gallery || !gallery.billeder || gallery.billeder.length === 0) {
    return null
  }

  const images = gallery.billeder

  const handlePrev = (e) => {
    e.stopPropagation()
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = (e) => {
    e.stopPropagation()
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  return (
    <section className="pt-6 pb-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {images.map((imgUrl, idx) => {
            const thumbnail = `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`
            return (
              <button
                key={idx}
                onClick={() => setSelectedIndex(idx)}
                className="group relative h-64 overflow-hidden border border-slate-200 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <img
                  src={thumbnail}
                  alt={`Galleri billede ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="w-12 h-12 bg-white text-black flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-lg text-black" aria-hidden="true" />
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Touch-friendly Lightbox Modal */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedIndex(null)}
        >
          <div className="relative max-w-5xl w-full flex flex-col items-center">
            {/* Close Button */}
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white text-2xl p-2 cursor-pointer focus:outline-none"
              aria-label="Luk galleri"
            >
              <i className="fa-solid fa-xmark text-3xl" aria-hidden="true" />
            </button>

            {/* Main Lightbox Image */}
            <div
              className="relative max-h-[80vh] w-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[selectedIndex]}
                alt={`Galleri ${selectedIndex + 1}`}
                className="max-h-[75vh] max-w-full object-contain"
              />

              {/* Prev Button */}
              <button
                onClick={handlePrev}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/70 hover:bg-sky-600 text-white flex items-center justify-center transition-colors cursor-pointer"
                aria-label="Forrige billede"
              >
                <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/70 hover:bg-sky-600 text-white flex items-center justify-center transition-colors cursor-pointer"
                aria-label="Næste billede"
              >
                <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
              </button>
            </div>

            {/* Image Indicator */}
            <div className="mt-4 text-white/80 text-xs font-semibold uppercase tracking-widest bg-slate-800/80 px-4 py-1.5">
              Billede {selectedIndex + 1} af {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
